import { api } from '@foundation/api';

export interface ConfigurationObject {
  configId: string;
  configName: string;
  configType: string;
  payload: Record<string, unknown>;
  version: number;
  lastModified: string;
  lastModifiedBy: string;
  businessUnit?: string;
  lastAccessedAt?: string;
  deletionScheduledAt?: string;
  deletedBy?: string;
}

export interface ListConfigsResponse {
  items: ConfigurationObject[];
  count: number;
}

export interface CreateConfigRequest {
  configId: string;
  configName: string;
  configType: string;
  payload: Record<string, unknown>;
  businessUnit?: string;
}

export interface UpdateConfigRequest {
  configName: string;
  configType: string;
  payload: Record<string, unknown>;
  version: number;
  businessUnit?: string;
}

export interface SoftDeleteRequest {
  reason: string;
}

/**
 * API service functions for /configs endpoints.
 */
export async function listConfigs(includePendingDeletions = false): Promise<ListConfigsResponse> {
  const searchParams = includePendingDeletions ? { includePendingDeletions: 'true' } : {};
  return api.get('configs', { searchParams }).json<ListConfigsResponse>();
}

export async function getConfig(configId: string): Promise<ConfigurationObject> {
  return api.get(`configs/${configId}`).json<ConfigurationObject>();
}

export async function createConfig(body: CreateConfigRequest): Promise<ConfigurationObject> {
  return api.post('configs', { json: body }).json<ConfigurationObject>();
}

export async function updateConfig(configId: string, body: UpdateConfigRequest): Promise<ConfigurationObject> {
  return api.put(`configs/${configId}`, { json: body }).json<ConfigurationObject>();
}

export async function softDeleteConfig(configId: string, reason: string): Promise<void> {
  await api.delete(`configs/${configId}`, { json: { reason } });
}

export async function restoreConfig(configId: string): Promise<ConfigurationObject> {
  return api.post(`configs/${configId}/restore`).json<ConfigurationObject>();
}

// --- New API functions for UX enhancements ---

export interface CountsResponse {
  counts: Record<string, number>;
  total: number;
}

export interface DependentConfig {
  configId: string;
  configName: string;
  configType: string;
  referencingField: string;
}

export interface DependentsResponse {
  dependents: DependentConfig[];
  count: number;
}

/**
 * List configs filtered by configType.
 */
export async function listConfigsByType(configType: string): Promise<ListConfigsResponse> {
  return api.get('configs', { searchParams: { configType } }).json<ListConfigsResponse>();
}

/**
 * Get per-type counts of configurations.
 */
export async function getConfigCounts(): Promise<CountsResponse> {
  return api.get('configs', { searchParams: { counts: 'true' } }).json<CountsResponse>();
}

/**
 * Get configurations that depend on (reference) the given configId.
 */
export async function getDependents(configId: string): Promise<DependentsResponse> {
  return api.get(`configs/${configId}/dependents`).json<DependentsResponse>();
}
